import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { debounceTime } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { IconKey } from '../../../../../whitelabel/icon-key.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { ButtonComponent } from '../../../../osp-ui/components/button/button/button.component';
import { CardOptionListComponent } from '../../../../osp-ui/components/form/card-option-list/card-option-list.component';
import { CardOptionComponent } from '../../../../osp-ui/components/form/card-option-list/components/card-option/card-option.component';
import { DropdownComponent } from '../../../../osp-ui/components/form/dropdown/dropdown.component';
import { ErrorComponent } from '../../../../osp-ui/components/form/form-field/components/error/error.component';
import { LabelComponent } from '../../../../osp-ui/components/form/form-field/components/label/label.component';
import { FormFieldComponent } from '../../../../osp-ui/components/form/form-field/form-field.component';
import { InputTextComponent } from '../../../../osp-ui/components/form/input-text/input-text.component';
import { StepComponent } from '../../../../osp-ui/components/panel/stepper/components/step/step.component';
import { StepContentDirective } from '../../../../osp-ui/components/panel/stepper/directives/step-content.directive';
import { StepFooterDirective } from '../../../../osp-ui/components/panel/stepper/directives/step-footer.directive';
import { StepperComponent } from '../../../../osp-ui/components/panel/stepper/stepper.component';
import { MapSearchPlaceResult } from '../../../../shared/components/maps/contract/map-search-place-result.contract';
import { MapLoaderService } from '../../../../shared/components/maps/services/map-loader.service';
import { Address } from '../../../../shared/contract/address.contract';
import { createDialogResult, DIALOG_ACTION, DialogResult } from '../../../../shared/contract/dialog-result.contract';
import { Authorities } from '../../../../shared/enums/authorities.enum';
import { IconPipe } from '../../../../shared/pipes/icon.pipe';
import { AddressService } from '../../../../shared/services/address.service';
import { LocationAddressFormComponent } from '../../shared/components/location-address-form/location-address-form.component';
import { ViewStock } from '../../../disposition/stocks/contract/view-stock.contract';
import { isLocationType, StockType } from '../../../disposition/stocks/enums/stock-type.enum';
import { StockApiService } from '../../../disposition/stocks/services/stock-api.service';
import { StockAddEditDialogFormController } from '../services/stock-add-edit-dialog-form-controller.service';
import { StockAddEditDialogFormOptionsService } from '../services/stock-add-edit-dialog-form-options.service';

@Component({
  selector: 'osp-stock-add-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    InputTextComponent,
    StepperComponent,
    StepComponent,
    StepContentDirective,
    StepFooterDirective,
    IconPipe,
    FormFieldComponent,
    LabelComponent,
    ErrorComponent,
    DropdownComponent,
    CardOptionListComponent,
    CardOptionComponent,
    NgTemplateOutlet,
    LocationAddressFormComponent,
  ],
  providers: [StockAddEditDialogFormController, StockAddEditDialogFormOptionsService],
  templateUrl: './stock-add-edit-dialog.component.html',
  styleUrls: ['./stock-add-edit-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockAddEditDialogComponent {
  readonly formService = inject(StockAddEditDialogFormController);
  readonly formOptionsService = inject(StockAddEditDialogFormOptionsService);
  private readonly dialogRef = inject(DynamicDialogRef<DialogResult<string>>);
  private readonly dialogConfig = inject<DynamicDialogConfig<{ stock?: ViewStock }>>(DynamicDialogConfig);
  private readonly stockApiService = inject(StockApiService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly mapLoaderService = inject(MapLoaderService);
  private readonly addressService = inject(AddressService);

  private readonly $stepper = viewChild(StepperComponent);

  protected readonly isEditMode = !!this.dialogConfig.data?.stock;
  protected readonly IconKey = IconKey;

  readonly form = this.formService.buildForm(this.dialogConfig.data?.stock);

  readonly $organisations = this.formOptionsService.$organisations;
  readonly stockTypes = this.formOptionsService.stockTypes;
  readonly isMapLoaded$ = this.mapLoaderService.isLoaded;

  mapCenter: google.maps.LatLngLiteral = { lat: 51.1657, lng: 10.4515 };
  mapMarkerPosition: google.maps.LatLngLiteral | null = null;
  private isAddressGeocodePending = false;
  private readonly $stockType = signal<StockType | null | undefined>(this.form.controls.stockType.value);

  readonly $selectedStockType = computed(() => {
    const type = this.$stockType();
    return this.stockTypes.find((t) => t.type === type)?.label || '-';
  });

  readonly $selectedStockTypeIcon = computed(() => {
    const type = this.$stockType();
    return this.stockTypes.find((t) => t.type === type)?.icon || '';
  });

  readonly $selectedStockTypeInfoDescription = computed(() => {
    const type = this.$stockType();

    if (!type) return '';
    return `modules.locations.stock.create.infoBox.expl.${type.toLowerCase()}`;
  });

  readonly $showAddressStep = computed(() => {
    const stockType = this.$stockType();
    return !!stockType && isLocationType(stockType);
  });

  readonly $showAutomaticGeofence = computed(
    () => !this.isEditMode && this.$showAddressStep() && this.authService.hasAuthority(Authorities.GEOFENCE_MANAGE),
  );

  constructor() {
    effect(() => {
      this.$showAddressStep();
      this.$stepper()?.refreshSteps();
    });

    const lat = this.form.controls.stockAddress.controls.lat.value;
    const lon = this.form.controls.stockAddress.controls.lon.value;
    if (this.hasValidLocation(lat, lon)) {
      this.setMapLocation(lat!, lon!);
    }

    this.form.controls.stockAddress.valueChanges
      .pipe(debounceTime(environment.DELAY_SHORTEST), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const address = this.addressService.format(value);
        if (!address.length || !this.isGoogleMapsLoaded()) {
          this.isAddressGeocodePending = false;
          this.updateAddressResolvedError(false);
          return;
        }

        this.isAddressGeocodePending = true;
        new google.maps.Geocoder().geocode({ address }, (results, status) => {
          this.isAddressGeocodePending = false;
          if (status !== 'OK' || !results?.length) {
            this.updateAddressResolvedError(true);
            return;
          }

          const location = results[0].geometry?.location;
          if (!location) return;

          const latResult = location.lat();
          const lonResult = location.lng();
          this.form.controls.stockAddress.patchValue({ lat: latResult, lon: lonResult }, { emitEvent: false });
          this.updateAddressResolvedError(false);
          this.setMapLocation(latResult, lonResult);
        });
      });

    this.updateAddressStepState(this.form.controls.stockType.value);
    this.form.controls.stockType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
      this.$stockType.set(type);
      this.updateAddressStepState(type);
    });
  }

  handleMapClick(event: google.maps.MapMouseEvent): void {
    if (!event.latLng) return;

    const lat = event.latLng.lat();
    const lon = event.latLng.lng();
    this.updateAddressFromCoordinates(lat, lon);
  }

  handleSearchLocationChange({ lat, lon, street, streetNumber, postalCode, city }: MapSearchPlaceResult): void {
    if (this.hasAnyAddressPart({ street, streetNumber, postalCode, city })) {
      this.form.controls.stockAddress.patchValue(
        { city, lat, lon, postalCode, street, streetNumber },
        { emitEvent: false },
      );
      this.setMapLocation(lat, lon);
      this.updateAddressResolvedError(false);
      return;
    }

    this.updateAddressFromCoordinates(lat, lon);
  }

  private updateAddressFromCoordinates(lat: number, lon: number): void {
    this.form.controls.stockAddress.patchValue({ lat, lon }, { emitEvent: false });
    this.setMapLocation(lat, lon);

    if (!this.isGoogleMapsLoaded()) return;

    new google.maps.Geocoder().geocode({ location: { lat, lng: lon } }, (results, status) => {
      if (status !== 'OK' || !results?.length) {
        this.updateAddressResolvedError(true);
        return;
      }

      const address = this.extractAddress(results[0]);
      this.form.controls.stockAddress.patchValue(
        {
          street: address.street,
          streetNumber: address.streetNumber,
          postalCode: address.postalCode,
          city: address.city,
          lat,
          lon,
        },
        { emitEvent: false },
      );
      this.updateAddressResolvedError(false);
    });
  }

  onCancel(): void {
    this.dialogRef.close(createDialogResult(DIALOG_ACTION.GO_BACK));
  }

  handleSubmit(): void {
    if (this.form.invalid || this.form.pending || this.isAddressGeocodePending) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isEditMode) {
      this.handleUpdate();
    } else {
      this.handleCreate();
    }
  }

  private handleCreate(): void {
    const command = this.formService.getCreateCommand();
    this.stockApiService
      .createStock(command)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dialogRef.close(createDialogResult(DIALOG_ACTION.SAVE));
        this.router.navigate(['/locations/active']);
      });
  }

  private handleUpdate(): void {
    const command = this.formService.getUpdateCommand();
    this.stockApiService
      .updateStock(command)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dialogRef.close(createDialogResult(DIALOG_ACTION.SAVE));
        this.router.navigate(['/locations/active']);
      });
  }

  private setMapLocation(lat: number, lng: number): void {
    this.mapCenter = { lat, lng };
    this.mapMarkerPosition = { lat, lng };
  }

  private hasValidLocation(lat?: number | null, lng?: number | null): boolean {
    return typeof lat === 'number' && Number.isFinite(lat) && typeof lng === 'number' && Number.isFinite(lng);
  }

  private hasAnyAddressPart(address: Partial<Address>): boolean {
    return Object.values(address).some((value) => `${value ?? ''}`.trim().length > 0);
  }

  private isGoogleMapsLoaded(): boolean {
    return typeof google !== 'undefined' && !!google.maps;
  }

  private updateAddressStepState(type: StockType | null | undefined): void {
    if (type && isLocationType(type)) {
      this.form.controls.stockAddress.controls.lat.addValidators(Validators.required);
      this.form.controls.stockAddress.controls.lon.addValidators(Validators.required);
      this.form.controls.stockAddress.controls.lat.updateValueAndValidity({ emitEvent: false });
      this.form.controls.stockAddress.controls.lon.updateValueAndValidity({ emitEvent: false });
      this.form.controls.stockAddress.enable({ emitEvent: false });
      return;
    }

    this.form.controls.stockAddress.disable({ emitEvent: false });
    this.form.controls.stockAddress.controls.lat.removeValidators(Validators.required);
    this.form.controls.stockAddress.controls.lon.removeValidators(Validators.required);
    this.form.controls.stockAddress.controls.lat.updateValueAndValidity({ emitEvent: false });
    this.form.controls.stockAddress.controls.lon.updateValueAndValidity({ emitEvent: false });
    this.updateAddressResolvedError(false);
  }

  private updateAddressResolvedError(isNotResolved: boolean): void {
    const currentErrors = this.form.controls.stockAddress.errors || {};
    if (isNotResolved) {
      this.form.controls.stockAddress.setErrors({ ...currentErrors, notResolved: true });
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { notResolved, ...restErrors } = currentErrors;
    this.form.controls.stockAddress.setErrors(Object.keys(restErrors).length ? restErrors : null);
  }

  private extractAddress(result: google.maps.GeocoderResult): Address {
    return {
      street: this.getAddressPart(result, 'route'),
      streetNumber: this.getAddressPart(result, 'street_number'),
      postalCode: this.getAddressPart(result, 'postal_code'),
      city:
        this.getAddressPart(result, 'locality') ||
        this.getAddressPart(result, 'postal_town') ||
        this.getAddressPart(result, 'administrative_area_level_2'),
    };
  }

  private getAddressPart(result: google.maps.GeocoderResult, type: string): string {
    const part = result.address_components.find((component) => component.types.includes(type));
    return part?.long_name || '';
  }
}
